<template>
  <f7-page name="shop-checkout">
    <f7-navbar :title="$t('checkout.title')" back-link :back-link-url="'#'" />
    <AppLayout>
      <template #main>
        <f7-block v-if="loading" class="text-align-center">
          <f7-preloader />
        </f7-block>

        <div v-else-if="!cart" class="empty-hint">
          <f7-icon f7="bag_x_fill" size="28" color="#8e8e93" />
          <div>{{ $t('shopPage.cartNotFound') }}</div>
        </div>

        <template v-else>
          <!-- Order items -->
          <div class="section-title">{{ $t('checkout.yourOrder') }}</div>
          <div class="item-list">
            <div v-for="item in cart.items" :key="item.id" class="cart-item">
              <div class="item-img">
                <img v-if="item.imageUrl" :src="item.imageUrl" />
                <f7-icon v-else f7="bag_fill" size="18" color="#8e8e93" />
              </div>
              <div class="item-body">
                <div class="item-name">{{ item.productName }}</div>
                <div class="item-variant" v-if="item.variantName">{{ item.variantName }}</div>
                <div class="item-price">{{ formatPrice(item.unitPrice) }}</div>
              </div>
              <div class="item-qty">
                <f7-stepper small :min="1" :max="99" :value="item.quantity" @stepper:change="(val) => updateQty(item.id, val)" />
              </div>
            </div>
          </div>

          <div v-if="!cart.items?.length" class="empty-hint">
            <f7-icon f7="bag_x_fill" size="28" color="#8e8e93" />
            <div>{{ $t('shop.panierVide') }}</div>
          </div>

          <!-- Delivery -->
          <div class="section-title">{{ $t('checkout.deliveryMethod') }}</div>
          <f7-list inset strong media-list >
            <f7-list-item
              v-for="option in deliveryOptions"
              :key="option.code"
              radio radio-icon="start"
              :title="option.label"
              :footer="formatPrice(option.fee)"
              name="delivery"
              :checked="deliveryOption === option.code"
              @change="onDeliveryChange(option.code)"
            />
          </f7-list>

          <!-- Delivery address (hidden for pickup) -->
          <template v-if="deliveryOption !== 'PICKUP'">
            <div class="section-title">{{ $t('checkout.deliveryAddress') }}</div>
            <f7-list inset strong media-list outline dividers form id="addressForm">
              <f7-list-input v-model:value="form.fullName" type="text" :label="$t('checkout.fullName')" required validate />
              <f7-list-input ref="phoneInputRef" v-model:value="form.phone" type="tel" :label="$t('checkout.phone')" required validate />
              <f7-list-input v-model:value="form.addressLine" type="text" :label="$t('checkout.addressLine')" required validate />
              <f7-list-input v-model:value="form.city" type="text" :label="$t('checkout.city')" required validate />
              <f7-list-input v-model:value="form.postalCode" type="text" :label="$t('checkout.postalCode')" />
            </f7-list>
          </template>

          <!-- Note -->
          <div class="section-title">{{ $t('checkout.note') }}</div>
          <f7-list inset strong>
            <f7-list-input v-model:value="note" type="textarea" :placeholder="$t('checkout.notePlaceholder')" />
          </f7-list>

          <!-- Summary -->
          <div class="summary-block">
            <div class="summary-row">
              <span>{{ $t('shop.sousTotal') }} ({{ itemCount }})</span>
              <span>{{ formatPrice(subtotal) }}</span>
            </div>
            <div class="summary-row">
              <span>{{ $t('shop.livraison') }}</span>
              <span>{{ deliveryFee === 0 ? $t('checkout.free') : formatPrice(deliveryFee) }}</span>
            </div>
            <div class="summary-total">
              <span>{{ $t('shop.total') }}</span>
              <span>{{ formatPrice(total) }}</span>
            </div>
          </div>
        </template>
      </template>
    </AppLayout>

    <!-- Sticky payment bar -->
    <f7-toolbar bottom class="checkout-toolbar" v-if="cart && !loading">
      <div class="toolbar-total">
        <span class="toolbar-total-label">{{ $t('shop.total') }}</span>
        <strong class="toolbar-total-value">{{ formatPrice(total) }}</strong>
      </div>
      <f7-button large fill round raised :disabled="!canPay || submitting" @click="submitCheckout">
        {{ submitting ? $t('common.loading') : $t('checkout.continueToPayment') }}
      </f7-button>
    </f7-toolbar>
  </f7-page>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { f7 } from 'framework7-vue';
import AppLayout from '@/components/common/AppLayout.vue';
import i18n from '@/js/i18n.js';
import { useCartStore } from '@/js/pinia-stores/cart.js';
import { useAuthStore } from '@/js/pinia-stores/auth.js';
import { useAccountStore } from '@/js/pinia-stores/account.js';
import { usePaymentTrigger } from '@/js/composables/usePaymentTrigger.js';
import { useTelephone } from '@/js/composables/useTelephone.js';

const t = i18n.global.t;
const props = defineProps({
  cartId: { type: String, default: null },
  f7route: Object,
  f7router: Object,
});

const cartStore = useCartStore();
const authStore = useAuthStore();
const accountStore = useAccountStore();
const trigger = usePaymentTrigger({ f7router: props.f7router });

const loading = ref(true);
const submitting = ref(false);
const cart = ref(null);
const note = ref('');
const deliveryOption = ref('LOCAL_DELIVERY');
const phoneInputRef = ref(null);
useTelephone(phoneInputRef);

// Fallback fee schedule (XOF) when the cart has no server-side fee yet.
const DELIVERY_FEES = {
  PICKUP: 0,
  LOCAL_DELIVERY: 1000,
  NATIONAL_DELIVERY: 2500,
};

const deliveryOptions = computed(() => [
  { code: 'PICKUP', label: t('shop.retraitBoutique'), fee: 0 },
  { code: 'LOCAL_DELIVERY', label: t('shop.livraisonLocale'), fee: DELIVERY_FEES.LOCAL_DELIVERY },
  { code: 'NATIONAL_DELIVERY', label: t('shop.livraisonNationale'), fee: DELIVERY_FEES.NATIONAL_DELIVERY },
]);

const form = ref({
  fullName: '',
  phone: '',
  addressLine: '',
  city: '',
  postalCode: '',
});

onMounted(async () => {
  try {
    let data = null;
    if (props.cartId) {
      data = await cartStore.get(props.cartId).catch(() => null);
    }
    if (!data) {
      const userId = authStore.profile?.sub;
      const userCarts = userId ? await cartStore.getByUser(userId).catch(() => []) : [];
      data = userCarts.find((c) => c.status === 'OPEN') || userCarts[0] || null;
    }
    cart.value = data;

    // Prefill contact from the account profile when available.
    const acct = accountStore.userAccount?.standard || accountStore.userAccount;
    form.value.fullName = form.value.fullName || [authStore.profile?.given_name, authStore.profile?.family_name].filter(Boolean).join(' ') || acct?.name || '';
    form.value.phone = form.value.phone || acct?.phoneNumber || '';
    form.value.city = form.value.city || acct?.city || '';
    form.value.addressLine = form.value.addressLine || acct?.address || '';
  } finally {
    loading.value = false;
  }
});

const subtotal = computed(() => Number(cart.value?.subtotal || 0));
const serverDeliveryFee = computed(() => {
  if (cart.value?.deliveryFee != null && deliveryOption.value === 'LOCAL_DELIVERY') return Number(cart.value.deliveryFee);
  return null;
});
const deliveryFee = computed(() => {
  if (serverDeliveryFee.value != null) return serverDeliveryFee.value;
  return DELIVERY_FEES[deliveryOption.value] ?? 0;
});
const total = computed(() => subtotal.value + deliveryFee.value);
const itemCount = computed(() => (cart.value?.items || []).reduce((sum, item) => sum + (item.quantity || 1), 0));

const canPay = computed(() => !!cart.value?.items?.length && !submitting.value);

function onDeliveryChange(code) {
  deliveryOption.value = code;
}

async function updateQty(itemId, qty) {
  if (!cart.value) return;
  try {
    await cartStore.updateItem(itemId, { quantity: qty });
    const item = cart.value.items?.find((i) => i.id === itemId);
    if (item) item.quantity = qty;
  } catch {
    f7.toast.show({ text: t('shop.erreurMiseAJour'), closeTimeout: 1500 });
  }
}

function validateForm() {
  if (deliveryOption.value === 'PICKUP') return true;
  if (!form.value.fullName || !form.value.phone || !form.value.addressLine || !form.value.city) {
    f7.dialog.alert(t('checkout.fillAddress'));
    return false;
  }
  return true;
}

async function submitCheckout() {
  if (!validateForm()) return;
  if (!cart.value?.id) {
    f7.dialog.alert(t('shopPage.cartNotFound'));
    return;
  }

  submitting.value = true;
  f7.preloader.show('multi');
  try {
    // 1. Create the order server-side from the cart.
    const order = await cartStore.checkout(cart.value.id).catch(() => null);

    // 2. Hand off to the unified payment page with an ORDER-scoped request.
    trigger.triggerCartCheckout({
      order,
      cart: cart.value,
      amount: total.value,
      currency: cart.value.currency || 'XOF',
      deliveryOption: deliveryOption.value,
      deliveryAddress:
        deliveryOption.value === 'PICKUP'
          ? null
          : {
              fullName: form.value.fullName,
              phone: form.value.phone,
              addressLine: form.value.addressLine,
              city: form.value.city,
              postalCode: form.value.postalCode,
            },
      note: note.value,
    });
  } catch (err) {
    console.error('Checkout failed:', err);
    f7.dialog.alert(err?.response?.data?.message || err?.message || t('shop.erreurCommande'));
  } finally {
    f7.preloader.hide();
    submitting.value = false;
  }
}

function formatPrice(amount) {
  const num = Math.round(Number(amount) || 0);
  try {
    return new Intl.NumberFormat(authStore.profile?.locale || 'fr-FR', {
      style: 'currency',
      currency: cart.value?.currency || 'XOF',
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `${num.toLocaleString('fr-FR')} ${cart.value?.currency}`;
  }
}
</script>

<style scoped>
.item-list { padding: 0 12px; }
.cart-item {
  display: flex; align-items: center; gap: 10px; padding: 10px;
  background: var(--f7-list-bg-color, #f5f5f5); border-radius: 10px; margin-bottom: 6px;
}
.item-img { width: 48px; height: 48px; border-radius: 8px; overflow: hidden; flex-shrink: 0; background: var(--f7-surface-container, #e0e0e0); display: flex; align-items: center; justify-content: center; }
.item-img img { width: 100%; height: 100%; object-fit: cover; }
.item-body { flex: 1; min-width: 0; }
.item-name { font-size: 12px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.item-variant { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); }
.item-price { font-size: 13px; font-weight: 500; color: var(--f7-theme-color); margin-top: 2px; }
.item-qty { flex-shrink: 0; }
.empty-hint { text-align: center; padding: 40px 16px; font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); }
.summary-block { background: var(--f7-list-bg-color, #f5f5f5); border-radius: 10px; padding: 12px 16px; margin: 8px 16px 96px; }
.summary-row { display: flex; justify-content: space-between; font-size: 12.5px; padding: 4px 0; color: var(--f7-on-surface-variant, #8e8e93); }
.summary-total { display: flex; justify-content: space-between; font-size: 15px; font-weight: 500; padding-top: 8px; margin-top: 4px; border-top: 1px solid var(--f7-input-border-color, #e0e0e0); }
.section-title { font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; color: var(--f7-on-surface-variant, #8e8e93); padding: 12px 16px 6px; }
.checkout-toolbar {
  --f7-toolbar-bg-color: var(--f7-sheet-bg-color, #fff);
  --f7-toolbar-height: 72px;
}
.toolbar-total { display: flex; flex-direction: column; line-height: 1.2; margin-right: 12px; white-space: nowrap; }
.toolbar-total-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--f7-on-surface-variant, #8e8e93); }
.toolbar-total-value { font-size: 17px; }
</style>
